/**
 * SyncStatusIndicator Component
 * Displays sync status to users in an accessible way
 * Shows when data is being synced or if there are pending items
 */

import React, { useState, useEffect } from 'react';
import { getSyncStatus } from '../utils/syncQueue';
import { isSyncInProgress } from '../utils/backgroundSync';

const SyncStatusIndicator = ({ showWhenEmpty = false }) => {
  const [syncStatus, setSyncStatus] = useState(getSyncStatus());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // Update status when sync queue changes
    const handleSyncStatusChange = (event) => {
      setSyncStatus(event.detail);
    };

    window.addEventListener('syncStatusChanged', handleSyncStatusChange);

    // Check sync progress periodically
    const interval = setInterval(() => {
      setSyncing(isSyncInProgress());
    }, 1000);

    return () => {
      window.removeEventListener('syncStatusChanged', handleSyncStatusChange);
      clearInterval(interval);
    };
  }, []);

  // Don't show if no pending items and showWhenEmpty is false
  if (!syncStatus.hasPendingItems && !showWhenEmpty) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'fixed',
        top: 'var(--spacing-md)',
        right: 'var(--spacing-md)',
        backgroundColor: syncing 
          ? 'var(--color-info-light)' 
          : syncStatus.hasPendingItems 
            ? 'var(--color-warning-light)' 
            : 'var(--color-success-light)',
        color: 'var(--color-text-primary)',
        padding: 'var(--spacing-md) var(--spacing-lg)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        border: `2px solid ${
          syncing 
            ? 'var(--color-info)' 
            : syncStatus.hasPendingItems 
              ? 'var(--color-warning)' 
              : 'var(--color-success)'
        }`,
        zIndex: 999,
        minWidth: '200px',
        maxWidth: '400px'
      }}
    >
      {/* Icon */}
      <span
        aria-hidden="true"
        style={{
          fontSize: 'var(--font-size-lg)',
          animation: syncing ? 'spin 2s linear infinite' : 'none'
        }}
      >
        {syncing ? '🔄' : syncStatus.hasPendingItems ? '⏳' : '✅'}
      </span>

      {/* Status text */}
      <span style={{ flex: 1 }}>
        {syncing 
          ? 'Syncing data...' 
          : syncStatus.hasPendingItems 
            ? `${syncStatus.pendingCount} ${syncStatus.pendingCount === 1 ? 'item' : 'items'} pending sync`
            : 'All data synced'}
      </span>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SyncStatusIndicator;
